import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FileText, Search, Eye, ChevronRight,
  Clock, CheckCircle, XCircle, Edit3, Send, Calendar,
  RefreshCw, CloudOff, Building2, ExternalLink, User, MapPin
} from 'lucide-react';
import {
  getFilteredDevis, getClientById, getIngenieurById, getSocieteById,
  statutDevisLabels, lastTiimeSync, getFilteredClients
} from '../data/mockData';
import { useFilters } from '../App';

const DevisList = () => {
  const filters = useFilters();
  const [search, setSearch] = useState('');
  const [filterStatut, setFilterStatut] = useState('tous');
  const [filterClient, setFilterClient] = useState('');
  const [filterAdresse, setFilterAdresse] = useState('');
  const [syncing, setSyncing] = useState(false);

  const allDevis = getFilteredDevis(filters);
  const allClients = getFilteredClients(filters);

  const formatMontant = (montant) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(montant);
  };

  // Options pour les filtres
  const clientOptions = allClients
    .filter(c => allDevis.some(d => d.clientId === c.id))
    .map(c => ({ value: c.id, label: c.nom }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const adresseOptions = [...new Set(allDevis.map(d => d.ville))]
    .sort()
    .map(v => ({ value: v, label: v }));

  const filteredDevis = allDevis.filter(d => {
    const client = getClientById(d.clientId);
    const matchSearch = d.numero.toLowerCase().includes(search.toLowerCase()) ||
      d.designation.toLowerCase().includes(search.toLowerCase()) ||
      client?.nom.toLowerCase().includes(search.toLowerCase());
    const matchStatut = filterStatut === 'tous' || d.statut === filterStatut;
    const matchClient = !filterClient || d.clientId === parseInt(filterClient);
    const matchAdresse = !filterAdresse || d.ville === filterAdresse;
    return matchSearch && matchStatut && matchClient && matchAdresse;
  });

  const countByStatut = (statut) => allDevis.filter(d => d.statut === statut).length;

  const getStatutIcon = (statut) => {
    switch (statut) {
      case 'brouillon': return <Edit3 size={14} />;
      case 'envoye': return <Send size={14} />;
      case 'accepte': return <CheckCircle size={14} />;
      case 'refuse': return <XCircle size={14} />;
      default: return null;
    }
  };

  const handleSync = () => {
    setSyncing(true);
    setTimeout(() => setSyncing(false), 2000);
  };

  const formatSyncDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('fr-FR') + ' à ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1><FileText size={28} /> Devis</h1>
          <div className="page-meta">
            <span>{allDevis.length} devis au total</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: 'var(--gray-500)' }}>
              <CloudOff size={14} /> Données importées depuis TIIME
            </span>
          </div>
        </div>
        <div className="page-actions">
          <button
            className="btn btn-primary"
            onClick={handleSync}
            disabled={syncing}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <RefreshCw size={18} className={syncing ? 'spin' : ''} />
            {syncing ? 'Synchronisation...' : 'Synchroniser depuis TIIME'}
          </button>
        </div>
      </div>

      {/* Info synchro */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0.75rem 1rem', marginBottom: '1rem',
        background: syncing ? '#FEF3C7' : '#F0FDF4',
        borderRadius: 'var(--radius)', border: `1px solid ${syncing ? '#FDE68A' : '#BBF7D0'}`,
        fontSize: '0.8125rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {syncing ? (
            <>
              <RefreshCw size={14} className="spin" style={{ color: '#D97706' }} />
              <span style={{ color: '#92400E' }}>Synchronisation en cours avec TIIME...</span>
            </>
          ) : (
            <>
              <CheckCircle size={14} style={{ color: '#16A34A' }} />
              <span style={{ color: '#166534' }}>Dernière synchronisation : {formatSyncDate(lastTiimeSync)}</span>
            </>
          )}
        </div>
        <a
          href="https://app.tiime.fr"
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: 'var(--primary)', textDecoration: 'none', fontWeight: 500, fontSize: '0.8125rem' }}
        >
          Ouvrir TIIME <ExternalLink size={12} />
        </a>
      </div>

      {/* Stats devis */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        {Object.entries(statutDevisLabels).map(([key, val]) => (
          <button
            key={key}
            className={`stat-card ${filterStatut === key ? 'selected' : ''}`}
            onClick={() => setFilterStatut(filterStatut === key ? 'tous' : key)}
            style={{ cursor: 'pointer', border: filterStatut === key ? `2px solid ${val.color}` : '1px solid var(--gray-200)', textAlign: 'left' }}
          >
            <div className="stat-icon" style={{ background: val.bg, color: val.color }}>
              {getStatutIcon(key)}
            </div>
            <div className="stat-content">
              <p className="stat-label">{val.label}</p>
              <p className="stat-value" style={{ fontSize: '1.5rem' }}>{countByStatut(key)}</p>
            </div>
          </button>
        ))}
      </div>

      {/* Filtres */}
      <div className="filters-bar">
        <div className="search-input-group">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder="Rechercher par numéro, désignation, client..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select className="filter-select" value={filterStatut} onChange={(e) => setFilterStatut(e.target.value)}>
          <option value="tous">Tous les statuts</option>
          <option value="brouillon">Brouillon</option>
          <option value="envoye">Envoyé</option>
          <option value="accepte">Accepté</option>
          <option value="refuse">Refusé</option>
        </select>
        <select
          className="filter-select"
          value={filterClient}
          onChange={(e) => setFilterClient(e.target.value)}
          style={{
            border: filterClient ? '2px solid var(--primary)' : undefined,
            background: filterClient ? 'var(--primary-50)' : undefined,
            color: filterClient ? 'var(--primary)' : undefined,
          }}
        >
          <option value="">Tous les clients</option>
          {clientOptions.map(c => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
        <select
          className="filter-select"
          value={filterAdresse}
          onChange={(e) => setFilterAdresse(e.target.value)}
          style={{
            border: filterAdresse ? '2px solid var(--primary)' : undefined,
            background: filterAdresse ? 'var(--primary-50)' : undefined,
            color: filterAdresse ? 'var(--primary)' : undefined,
          }}
        >
          <option value="">Toutes les adresses</option>
          {adresseOptions.map(a => (
            <option key={a.value} value={a.value}>{a.label}</option>
          ))}
        </select>
      </div>

      {/* Liste des devis */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {filteredDevis.map(d => {
          const client = getClientById(d.clientId);
          const ingenieur = getIngenieurById(d.ingenieurId);
          const societe = getSocieteById(d.societeId);
          const statut = statutDevisLabels[d.statut];

          return (
            <div key={d.id} className="offre-card">
              <div className="offre-card-header">
                <div className="offre-ref">
                  <span className="offre-reference">{d.numero}</span>
                  <span className="statut-badge" style={{ background: statut.bg, color: statut.color }}>
                    {getStatutIcon(d.statut)} {statut.label}
                  </span>
                  {d.codeMission && (
                    <span className="badge badge-secondary">{d.codeMission}</span>
                  )}
                  <span className="badge" style={{
                    background: societe?.id === 1 ? '#DBEAFE' : '#FFF7ED',
                    color: societe?.id === 1 ? '#1E40AF' : '#9A3412',
                    fontSize: '0.6875rem'
                  }}>
                    {societe?.nom}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontWeight: 700, fontSize: '1.125rem', color: 'var(--primary)' }}>
                    {formatMontant(d.montantHT)}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>HT</span>
                </div>
              </div>
              <h3 className="offre-titre">{d.designation}</h3>
              <div className="offre-meta">
                <span className="offre-meta-item">
                  <Users2Icon size={14} />
                  {client?.nom}
                </span>
                <span className="offre-meta-item">
                  <MapPinIcon size={14} />
                  {d.ville}
                </span>
                <span className="offre-meta-item" style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontWeight: 500 }}>
                  <div style={{
                    width: '20px', height: '20px', borderRadius: '50%',
                    background: ingenieur?.couleur || '#6B7280', color: 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.625rem', fontWeight: 600, flexShrink: 0
                  }}>
                    {ingenieur?.initiales}
                  </div>
                  <UserIcon size={14} />
                  {ingenieur?.prenom} {ingenieur?.nom} — {ingenieur?.role}
                </span>
                {d.tiimeId && (
                  <span className="offre-meta-item" style={{ color: 'var(--gray-400)', fontSize: '0.75rem' }}>
                    TIIME: {d.tiimeId}
                  </span>
                )}
              </div>
              <div className="offre-footer">
                <div style={{ display: 'flex', gap: '1rem', fontSize: '0.8125rem', color: 'var(--gray-500)' }}>
                  {d.dateCreation && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <Calendar size={14} /> Créé le {new Date(d.dateCreation).toLocaleDateString('fr-FR')}
                    </span>
                  )}
                  {d.dateEnvoi && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <Send size={14} /> Envoyé le {new Date(d.dateEnvoi).toLocaleDateString('fr-FR')}
                    </span>
                  )}
                  {d.dateRelance && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: 'var(--warning)' }}>
                      <Clock size={14} /> Relancé le {new Date(d.dateRelance).toLocaleDateString('fr-FR')}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {d.statut === 'accepte' && (
                    <Link to={`/affaires`} className="btn btn-sm btn-success">
                      Voir l'affaire <ChevronRight size={14} />
                    </Link>
                  )}
                  <Link to={`/devis/${d.id}`} className="btn btn-sm btn-secondary">
                    <Eye size={14} /> Détails
                  </Link>
                </div>
              </div>
            </div>
          );
        })}
        {filteredDevis.length === 0 && (
          <div className="empty-state">
            <FileText size={48} />
            <h3>Aucun devis trouvé</h3>
            <p>Modifiez vos filtres ou synchronisez depuis TIIME.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Mini icons for meta
const Users2Icon = ({ size }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
const MapPinIcon = ({ size }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>;
const UserIcon = ({ size }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;

export default DevisList;
